import { useEffect, useState, useCallback } from "react";
import { Polygon, Polyline, CircleMarker } from "react-leaflet";
import LeafletMap, { FitBounds, ZONE_PALETTE } from "@/components/map/LeafletMap";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Loader2, Scissors, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { fmtMoney, scoreColor } from "@/lib/prospects";
import BusinessLayers from "@/components/map/BusinessLayers";

// Two-click divider-line splitter. Admin clicks one edge then the opposite edge;
// the backend computes the resulting polygons + per-piece metrics for preview,
// then runs the hardened split on confirm.
export default function ZoneDividerSplit({ zone, open, onOpenChange, onDone, businesses = [], onShopAction }) {
  const [detail, setDetail] = useState(null);
  const [nearbyZones, setNearbyZones] = useState([]);
  const [pts, setPts] = useState([]);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    if (!open) { setDetail(null); setNearbyZones([]); setPts([]); setPreview(null); setBusy(false); setConfirming(false); return; }
    (async () => {
      try {
        const { data } = await api.get(`/admin/zones/${zone.zoneId}/details`); setDetail(data.zone);
        if (data.zone?.bikePathId) api.get("/admin/zones/nearby", { params: { bike_path_id: data.zone.bikePathId } }).then((r) => setNearbyZones(r.data || [])).catch(() => {});
      }
      catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    })();
  }, [open, zone?.zoneId]);

  const polygon = detail?.polygon || [];
  const boundary = detail?.bikePathBoundary || [];

  const runPreview = useCallback(async (p1, p2) => {
    setBusy(true); setPreview(null);
    try {
      const { data } = await api.post(`/zones/${zone.zoneId}/split-divider/preview`, { p1, p2 });
      setPreview(data);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); setPts([]); }
    finally { setBusy(false); }
  }, [zone?.zoneId]);

  const onMapClick = (lat, lng) => {
    if (preview || busy) return;
    setPts((cur) => {
      const next = cur.length >= 2 ? [[lat, lng]] : [...cur, [lat, lng]];
      if (next.length === 2) runPreview(next[0], next[1]);
      return next;
    });
  };

  const extendedLine = () => {
    if (pts.length < 2) return null;
    const [[y1, x1], [y2, x2]] = pts;
    const dx = x2 - x1, dy = y2 - y1, ext = 1.5;
    return [[y1 - dy * ext, x1 - dx * ext], [y2 + dy * ext, x2 + dx * ext]];
  };

  const confirm = async () => {
    setConfirming(true);
    try {
      await api.post(`/zones/${zone.zoneId}/split-divider`, { p1: pts[0], p2: pts[1] });
      toast.success(`Zone ${zone.zoneNumber} split into ${preview.pieces.length} pieces`);
      onDone?.();
      onOpenChange(false);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setConfirming(false); }
  };

  const reset = () => { setPts([]); setPreview(null); };
  const bounds = polygon.length >= 2 ? polygon : boundary;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[86vh] top-[62%] overflow-y-auto" data-testid="zone-divider-dialog">
        <DialogHeader>
          <DialogTitle>Split Zone {zone?.zoneNumber} — divider line</DialogTitle>
          <DialogDescription>
            {preview ? "Review the resulting pieces below, then confirm the split." :
              pts.length === 0 ? "Click one edge of the highlighted zone." :
              pts.length === 1 ? "Now click the opposite edge." : "Computing…"}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3" data-testid="zone-divider-split">
          <LeafletMap height="52vh" onClick={onMapClick}>
            {bounds.length >= 2 && <FitBounds bounds={bounds} nonce={open ? 1 : 0} />}
            {boundary.length >= 3 && <Polygon positions={boundary} pathOptions={{ color: "#64748B", weight: 2, fillOpacity: 0.04, dashArray: "6", interactive: false }} />}
            {nearbyZones.map((z) => ((z.polygon || []).length >= 3 ? <Polygon key={`nb-${z.id}`} positions={z.polygon} pathOptions={{ color: "#A855F7", fillColor: "#A855F7", fillOpacity: 0.15, weight: 2, dashArray: "5 4", interactive: false }} /> : null))}
            {!preview && polygon.length >= 3 && <Polygon positions={polygon} pathOptions={{ color: "#EF4444", fillColor: "#FACC15", fillOpacity: 0.18, weight: 2, interactive: false }} />}
            {preview?.pieces?.map((pc, i) => (
              <Polygon key={i} positions={pc.polygon} pathOptions={{ color: ZONE_PALETTE[i % ZONE_PALETTE.length], fillColor: ZONE_PALETTE[i % ZONE_PALETTE.length], fillOpacity: 0.35, weight: 2, interactive: false }} />
            ))}
            {businesses.length > 0 && <BusinessLayers businesses={businesses} onShopAction={onShopAction} />}
            {extendedLine() && <Polyline positions={extendedLine()} pathOptions={{ color: "#EF4444", weight: 3, dashArray: "6", interactive: false }} />}
            {pts.map((p, i) => <CircleMarker key={i} center={p} radius={6} pathOptions={{ color: "#fff", fillColor: "#EF4444", fillOpacity: 1, weight: 2 }} />)}
          </LeafletMap>

          {nearbyZones.length > 0 && (
            <div className="text-xs text-muted-foreground flex flex-wrap items-center gap-1.5" data-testid="split-nearby-legend">
              <span className="w-3 h-3 rounded-sm" style={{ backgroundColor: "rgba(168,85,247,0.25)", border: "1px dashed #A855F7" }} />
              Purple dashed = zones on other bike paths ({nearbyZones.map((z) => `${z.name}${z.bikePath ? ` · ${z.bikePath}` : ""}`).join(", ")}).
            </div>
          )}

          {busy && <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" />Computing split…</div>}

          {preview && (
            <div className="grid sm:grid-cols-2 gap-3" data-testid="divider-preview-panel">
              {preview.pieces.map((pc, i) => (
                <div key={i} className="rounded-xl border p-3 bg-card" style={{ borderColor: ZONE_PALETTE[i % ZONE_PALETTE.length] }} data-testid={`divider-piece-${i}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-heading font-bold tabular-nums" style={{ color: ZONE_PALETTE[i % ZONE_PALETTE.length] }}>
                      {pc.zoneNumber ? `Zone ${pc.zoneNumber}` : (pc.isRetained ? zone?.name || "Original zone" : "New zone")}{pc.isRetained ? "" : pc.zoneNumber ? " (new)" : ""}
                    </span>
                    <span className="text-[10px] uppercase tracking-wide text-muted-foreground">{pc.isRetained ? "retained" : "created"}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div><div className="text-muted-foreground">Businesses</div><div className="tabular-nums font-medium" data-testid={`divider-piece-${i}-biz`}>{pc.businessCount}</div></div>
                    <div><div className="text-muted-foreground">Prospects</div><div className="tabular-nums font-medium" data-testid={`divider-piece-${i}-prospects`}>{pc.prospectCount}</div></div>
                    <div><div className="text-muted-foreground">Est $/mo</div><div className="tabular-nums text-emerald-500 font-medium" data-testid={`divider-piece-${i}-rev`}>{fmtMoney(pc.projectedMonthly)}</div></div>
                    <div><div className="text-muted-foreground">Opportunity</div><div className={`tabular-nums font-bold ${scoreColor(pc.opportunityScore)}`} data-testid={`divider-piece-${i}-opp`}>{pc.opportunityScore}</div></div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sticky bottom-0 bg-background pt-3 -mb-2 border-t border-border/50 z-10">
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="divider-cancel">Cancel</Button>
          {(pts.length > 0 || preview) && <Button variant="outline" onClick={reset} data-testid="divider-reset"><RotateCcw className="w-4 h-4 mr-1" />Reset points</Button>}
          <Button onClick={confirm} disabled={!preview || confirming} data-testid="divider-confirm">
            {confirming ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Scissors className="w-4 h-4 mr-2" />}Confirm Split
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
